import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { useAddExistingWaTemplate, useSearchChat360Templates } from "../../../lib/api";
import { StatusPill } from "./status";
import { focusRing } from "./ui";
import { typeLabel } from "./templateFormat";

// The stage screens open this dialog through the context, so one dialog serves every template
// picker. `open(onAdded)` shows it; `onAdded(row)` runs once the template has been added.
export const AddTemplateContext = createContext(null);
export const useAddTemplateDialog = () => useContext(AddTemplateContext);

const message = (err) => err?.response?.data?.detail || "Could not add the template. Try again in a moment.";

// Why a result cannot be added, in a few words; empty when it can.
function blocked(t) {
  if (t.added) return "Already added";
  if (t.taken_by_other_brand) return "Used by another brand";
  if (!t.usable) return t.unusable_reason;
  return "";
}

// Adds a template that is already on the brand's Chat360 number. Type a part of its name, see what
// Chat360 has, press Add. Nothing is made or sent to Meta: its text and status are read from Chat360.
export default function AddExistingTemplate({ tenantId, onClose, onAdded }) {
  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  const [error, setError] = useState("");
  const [adding, setAdding] = useState("");
  const input = useRef(null);
  const add = useAddExistingWaTemplate(tenantId);
  const { data: rows, isFetching, isError, error: searchError } = useSearchChat360Templates(tenantId, term);

  useEffect(() => {
    input.current?.focus();
    const onKey = (e) => e.key === "Escape" && !add.isPending && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [add.isPending, onClose]);

  // Search a moment after typing stops, so each letter does not ask Chat360.
  useEffect(() => {
    const id = setTimeout(() => setTerm(text.trim()), 350);
    return () => clearTimeout(id);
  }, [text]);

  const pick = async (t) => {
    setError("");
    setAdding(t.name);
    try {
      const out = await add.mutateAsync(t.name);
      toast.success(out.already ? `${t.name} was already added.` : `${t.name} added.`);
      onAdded?.(out.row);
      onClose();
    } catch (err) {
      setError(message(err));
    } finally {
      setAdding("");
    }
  };

  // Enter adds the result whose name is exactly what was typed.
  const onEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const exact = (rows || []).find((t) => t.name === text.trim() && !blocked(t));
    if (exact) pick(exact);
  };

  const waiting = text.trim() !== term || isFetching;
  const ready = term.length >= 2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="tpl-existing-title" className="flex max-h-[90vh] w-full max-w-[560px] flex-col rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="tpl-existing-title" className="m-0 text-[17px] font-semibold text-[#111827]">
              Add a template from Chat360
            </h3>
            <p className="mb-0 mt-2 text-[14px] leading-6 text-[#4B5563]">
              For a template you already made on Chat360. Nothing is created or sent to Meta. Its text and approval status are copied from Chat360.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={add.isPending}
            className={`flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#6B7280] hover:bg-[#F3F4F6] ${focusRing}`}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <label htmlFor="tpl-existing-name" className="mb-2 mt-5 block text-[13.5px] font-medium text-[#374151]">
          Template name on Chat360
        </label>
        <div className="relative">
          <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            id="tpl-existing-name"
            ref={input}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError("");
            }}
            onKeyDown={onEnter}
            autoComplete="off"
            spellCheck={false}
            placeholder="Type part of the name, for example maruti_welcome"
            className={`h-11 w-full rounded-lg border border-[#D0D5DD] bg-white pl-9 pr-3 font-mono text-[14px] text-[#111827] ${focusRing}`}
          />
        </div>

        <div className="mt-3 min-h-[120px] flex-1 overflow-y-auto" aria-live="polite">
          {!ready && <p className="m-0 py-6 text-center text-[14px] text-[#6B7280]">Type at least 2 letters of the name.</p>}
          {ready && waiting && !rows && <p className="m-0 py-6 text-center text-[14px] text-[#6B7280]">Looking on Chat360...</p>}
          {ready && isError && !waiting && (
            <p role="alert" className="m-0 py-6 text-center text-[14px] text-[#B42318]">
              {message(searchError)}
            </p>
          )}
          {ready && !isError && !waiting && rows && rows.length === 0 && (
            <p className="m-0 py-6 text-center text-[14px] leading-6 text-[#6B7280]">
              Chat360 has no template with a name like that on this brand's WhatsApp number. Check the spelling.
            </p>
          )}
          {ready && !isError && rows && rows.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Templates on Chat360">
              {rows.map((t) => {
                const why = blocked(t);
                return (
                  <li key={t.name} className="flex items-start gap-3 rounded-xl border border-[#EAECF0] p-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="break-all font-mono text-[14px] font-semibold text-[#111827]">{t.name}</span>
                        <StatusPill status={t.status} />
                      </div>
                      <div className="mt-1 text-[12.5px] capitalize text-[#6B7280]">
                        {String(t.category).toLowerCase()} · {typeLabel(t.template_type)}
                      </div>
                      {t.body && <p className="mb-0 mt-2 line-clamp-2 whitespace-pre-line text-[13.5px] leading-5 text-[#374151]">{t.body}</p>}
                      {why && <p className="mb-0 mt-2 text-[12.5px] text-[#92400E]">{why}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => pick(t)}
                      disabled={!!why || add.isPending}
                      className={`h-9 shrink-0 cursor-pointer rounded-full bg-[#2667B5] px-4 text-[13.5px] font-semibold text-white hover:bg-[#1F5596] disabled:cursor-not-allowed disabled:bg-[#D0D5DD] ${focusRing}`}
                    >
                      {adding === t.name ? "Adding..." : "Add"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {error && (
          <p role="alert" className="mb-0 mt-3 rounded-lg bg-[#FEF3F2] px-3 py-2 text-[13.5px] leading-5 text-[#B42318]">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
